import React from "react";
import { formatMessage } from "lib/globalize-util";
import {
    FocusZone,
    mergeStyleSets,
    ShimmeredDetailsList,
    IColumn,
    SelectionMode,
    Selection,
    IBreadcrumbItem,
    CheckboxVisibility,
    Text,
    IDetailsListProps,
    DetailsRow,
    IDetailsRowProps,
    Spinner,
    nuOrderTheme as theme,
    Checkbox,
    Icon,
    Link,
} from "@nuorder/components";
import { ROOT_ITEM, SpaceView } from "containers/folders/hooks/utils";
import { DATATEST } from "constants/integration";
import { buildBreadcrumb } from "containers/folders/utils/drive-navigation";
import { ItemKind } from "containers/folders/types/base";
import { FolderNavigationBreadcrumb, IFolderNavigationBreadcrumbProps } from "./folder-navigation-breadcrumb";
import { getItemIconName, nameExtRemover } from "containers/folders/utils/space-item";
export interface IFolderSelection extends Partial<nuorder.folder.v1.Item> {
    spaceView: SpaceView;
    icon?: string;
    isDrive: boolean;
    isSharedRoot?: boolean;
}

interface IFolderNavigationProps {
    selection: Selection;
    folders: IFolderSelection[];
    isFetchingData: boolean;
    errorMessage?: string;
    navigation: IFolderSelection[];
    selectionMode?: SelectionMode;
    selectionKind?: ItemKind;
    noMargin?: boolean;
    canFetchMore?: boolean;
    onGoToSpaceItem: (itemId: string, spaceView: SpaceView, isBackNavigation?: boolean) => void;
    onFetchNextResults: () => void;
    onRenderWayfinding?: (props: IFolderNavigationBreadcrumbProps) => React.ReactElement;
    onRowSelected?: (item: IFolderSelection, checked: boolean) => void;
    itemsSelected?: IFolderSelection[];
}

export const FolderNavigation: React.FunctionComponent<IFolderNavigationProps> = ({
    folders,
    selection,
    isFetchingData,
    errorMessage,
    navigation,
    selectionMode = SelectionMode.single,
    selectionKind = ItemKind.Folder,
    noMargin,
    canFetchMore,
    onGoToSpaceItem,
    onFetchNextResults,
    onRenderWayfinding,
    onRowSelected,
    itemsSelected,
}) => {
    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                browser: {
                    margin: noMargin ? 0 : "12px 0 24px 0",
                    height: 250,
                    flex: "1 1 auto",
                    overflowX: "hidden",
                    overflowY: "auto",
                },
                loading: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                },
                row: {
                    cursor: "pointer",
                },
                fileIconCell: {
                    textAlign: "center",
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                    display: "flex !important",
                    alignItems: "center",
                    justifyContent: "center",
                },
                fileIcon: {
                    fontSize: 16,
                },
                errorMessage: {
                    fontSize: 14,
                    color: theme.palette.redDark,
                    paddingTop: 10,
                    wordBreak: "break-word",
                },
                link: {
                    fontWeight: "normal",
                    color: theme.palette.black,
                    fontSize: "14px !important",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    "&:focus": {
                        color: theme.palette.black,
                    },
                },
            }),
        [noMargin]
    );

    const handleItemInvoked = React.useCallback(
        (item: IFolderSelection) => {
            if (item.isDrive || item.kind === ItemKind.Folder) {
                onGoToSpaceItem(item.id, item.isDrive ? item.spaceView : null);
                setBreadcrumb((items) =>
                    buildBreadcrumb(
                        items,
                        [
                            {
                                ...item,
                                id: item.isDrive ? `${item.id}_${item.spaceView}` : item.id,
                            },
                        ],
                        handleBreadcrumbItemClicked
                    )
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onGoToSpaceItem]
    );

    const iconRenderer = React.useCallback((item: IFolderSelection) => {
        const iconName = item.isDrive ? item.icon : getItemIconName(item);
        return <Icon iconName={iconName} className={classes.fileIcon} />;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const nameRenderer = React.useCallback(
        (item: IFolderSelection) => {
            const itemName = nameExtRemover(item.name, item.kind as ItemKind);
            // DetailsList onItemInvoked responsive issue nothttps://github.com/microsoft/fluentui/issues/8858
            if (item.kind === ItemKind.Folder || item.isDrive) {
                return (
                    <Link
                        data-test={DATATEST.FOLDERS.ASSET_ROW.NAME}
                        data-selection-disabled={true}
                        title={itemName}
                        className={classes.link}
                        onClick={() => handleItemInvoked(item)}
                    >
                        {itemName}
                    </Link>
                );
            }
            return (
                <Text variant="medium" nowrap block data-test={DATATEST.FOLDERS.ASSET_ROW.NAME}>
                    {itemName}
                </Text>
            );
        },
        [handleItemInvoked, classes]
    );

    const checkRenderer = React.useCallback(
        (item: IFolderSelection) => {
            if (item.isDrive || item.kind !== selectionKind) {
                return null;
            }

            return (
                <Checkbox
                    key={item.id}
                    onChange={(_ev, checked) => {
                        onRowSelected && onRowSelected(item, checked);
                    }}
                    checked={itemsSelected.some((it) => it.id === item.id)}
                />
            );
        },
        [selectionKind, onRowSelected, itemsSelected]
    );

    const columnDefinitions = React.useMemo<IColumn[]>(
        () => [
            ...(selectionMode === SelectionMode.multiple
                ? [
                      {
                          key: "check",
                          name: "",
                          iconName: "",
                          fieldName: "id",
                          minWidth: 16,
                          maxWidth: 16,
                          onRender: checkRenderer,
                      },
                  ]
                : []),
            {
                key: "icon",
                name: "",
                className: classes.fileIconCell,
                iconName: "",
                isIconOnly: true,
                fieldName: "kind",
                minWidth: 16,
                maxWidth: 16,
                onRender: iconRenderer,
            },
            {
                key: "name",
                name: formatMessage("Name"),
                fieldName: "name",
                minWidth: 150,
                isResizable: true,
                onRender: nameRenderer,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [classes, selectionMode, checkRenderer]
    );

    const handleRenderCustomPlaceholder = React.useCallback(
        (rowProps: IDetailsRowProps, index: number, defaultRender: (props: IDetailsRowProps) => React.ReactNode) => {
            onFetchNextResults();
            return defaultRender(rowProps);
        },
        [onFetchNextResults]
    );

    const handleRenderRow: IDetailsListProps["onRenderRow"] = React.useCallback((props: IDetailsRowProps) => {
        if (props) {
            return (
                <div data-id={props.item.id} data-test={DATATEST.FOLDERS.ASSET_ROW.ROW} className={classes.row}>
                    <DetailsRow data-test={props.item.spaceView} {...props} />
                </div>
            );
        }
        return null;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [breadcrumb, setBreadcrumb] = React.useState<IBreadcrumbItem[]>([
        {
            text: formatMessage("Home"),
            key: ROOT_ITEM,
        },
    ]);

    const handleBreadcrumbItemClicked = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>, item: IBreadcrumbItem) => {
            const [itemId, spaceView] = item.key.split("_");
            onGoToSpaceItem(itemId, spaceView ? (spaceView as SpaceView) : null, true);
            setBreadcrumb((items) => {
                const index = items.findIndex((it) => it.key === item.key);
                return buildBreadcrumb(items.slice(0, index + 1));
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setBreadcrumb, navigation]
    );

    React.useEffect(() => {
        setBreadcrumb((items) =>
            buildBreadcrumb(
                items,
                navigation.map((item) => ({
                    ...item,
                    id: item.isDrive ? `${item.id}_${item.spaceView}` : item.id,
                    name: item.name,
                })),
                handleBreadcrumbItemClicked
            )
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const totalItems = folders.filter((it) => it).length;
    const validItems = folders.filter((it) => it?.kind === selectionKind);
    const renderWayfinding = React.useCallback(() => {
        const props: IFolderNavigationBreadcrumbProps = {
            breadcrumb: breadcrumb,
            totalItems: folders.filter((it) => it).length,
            isFetchingData,
            isSellectAllDisabled: validItems.length === 0,
            validItems,
            canFetchMore,
        };
        if (!onRenderWayfinding) {
            return <FolderNavigationBreadcrumb {...props} />;
        }

        return onRenderWayfinding(props);
    }, [onRenderWayfinding, breadcrumb, totalItems, validItems, isFetchingData, canFetchMore]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div data-test={DATATEST.FOLDERS.DIALOG.CONTAINER}>
            <div data-test={DATATEST.FOLDERS.SELECTION_MAIN_CONTAINER} data-has-more-records={canFetchMore}>
                {renderWayfinding()}
                <div
                    className={classes.browser}
                    data-test={DATATEST.FOLDERS.SELECTION_ITEMS_CONTAINER}
                    data-is-scrollable="true"
                >
                    {isFetchingData && !folders.length && (
                        <div className={classes.loading}>
                            <Spinner label={formatMessage("Loading...")} />
                        </div>
                    )}
                    <FocusZone>
                        <ShimmeredDetailsList
                            selectionMode={selectionMode}
                            items={folders}
                            isHeaderVisible={false}
                            columns={columnDefinitions}
                            checkboxVisibility={CheckboxVisibility.hidden}
                            onRenderCustomPlaceholder={handleRenderCustomPlaceholder}
                            selectionPreservedOnEmptyClick={true}
                            selection={selection}
                            onItemInvoked={handleItemInvoked}
                            onRenderRow={handleRenderRow}
                        />
                    </FocusZone>
                </div>
                {!!errorMessage && (
                    <div data-test={DATATEST.FOLDERS.DIALOG.ERROR} className={classes.errorMessage}>
                        {errorMessage}
                    </div>
                )}
            </div>
        </div>
    );
};
